import React, { useState, useEffect, useCallback } from 'react';
import './RsvpModal.css';

const RsvpModal = ({
  open,
  initialComing,
  initialGuestCount,
  initialNote,
  maxGuestCount = 10,
  onClose,
  onConfirm,
  onDecline,
  saving = false,
  error = null,
}) => {
  const [guestCount, setGuestCount] = useState(1);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Initialize fields when modal opens
  useEffect(() => {
    if (open) {
      setGuestCount(
        initialComing === true && initialGuestCount ? initialGuestCount : 1
      );
      setNote(initialNote || '');
      setValidationError(null);
      setIsClosing(false);
    }
  }, [open, initialComing, initialGuestCount, initialNote]);

  const handleClose = useCallback(() => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose, saving]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open && !saving) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, saving, handleClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !saving) {
      handleClose();
    }
  };

  const handleConfirm = () => {
    if (guestCount < 1) {
      setValidationError('Molimo odaberi broj gostiju.');
      return;
    }
    setValidationError(null);
    onConfirm(guestCount, note);
  };

  const handleDecline = () => {
    setValidationError(null);
    onDecline(note);
  };

  const incrementCount = () => {
    if (guestCount < maxGuestCount) {
      setGuestCount((prev) => prev + 1);
      setValidationError(null);
    }
  };

  const decrementCount = () => {
    if (guestCount > 0) {
      setGuestCount((prev) => prev - 1);
    }
  };

  const handleCountChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= maxGuestCount) {
      setGuestCount(value);
      setValidationError(null);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className={`rsvp-modal-overlay ${isClosing ? 'rsvp-modal-overlay--closing' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rsvp-modal-title"
    >
      <div className={`rsvp-modal ${isClosing ? 'rsvp-modal--closing' : ''}`}>
        {/* Close button */}
        <button
          className="rsvp-modal__close"
          onClick={handleClose}
          disabled={saving}
          aria-label="Zatvori"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Title */}
        <h2 id="rsvp-modal-title" className="rsvp-modal__title">
          Potvrda dolaska
        </h2>

        {/* Adults Only Note */}
        <p className="rsvp-modal__note">*Adults only</p>

        {/* Guest count stepper */}
        <div className="rsvp-modal__section">
          <label className="rsvp-modal__label">Broj gostiju</label>
          <div className="rsvp-modal__stepper">
            <button
              type="button"
              className="rsvp-modal__stepper-btn"
              onClick={decrementCount}
              disabled={guestCount <= 0 || saving}
              aria-label="Smanji broj gostiju"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14" />
              </svg>
            </button>
            <input
              type="number"
              className="rsvp-modal__stepper-value"
              value={guestCount}
              onChange={handleCountChange}
              min="0"
              max={maxGuestCount}
              disabled={saving}
              readOnly
              aria-label="Broj gostiju"
            />
            <button
              type="button"
              className="rsvp-modal__stepper-btn"
              onClick={incrementCount}
              disabled={guestCount >= maxGuestCount || saving}
              aria-label="Povecaj broj gostiju"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
          {validationError && (
            <p className="rsvp-modal__validation-error">{validationError}</p>
          )}
        </div>

        {/* Special note textarea */}
        <div className="rsvp-modal__section">
          <label className="rsvp-modal__label" htmlFor="rsvp-note">
            Napomena
          </label>
          <textarea
            id="rsvp-note"
            className="rsvp-modal__textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Imas li kakvu napomenu ili posebnu zelju? (npr. alergije, prehrana, dolazak kasnije...)"
            disabled={saving}
          />
        </div>

        {/* Error message */}
        {error && (
          <p className="rsvp-modal__error">
            Doslo je do greske pri spremanju. Pokusaj ponovno.
          </p>
        )}

        {/* Action buttons */}
        <div className="rsvp-modal__actions">
          <button
            type="button"
            className="rsvp-modal__btn rsvp-modal__btn--secondary"
            onClick={handleDecline}
            disabled={saving}
          >
            {saving ? 'Spremanje...' : 'Odbij dolazak'}
          </button>
          <button
            type="button"
            className="rsvp-modal__btn rsvp-modal__btn--primary"
            onClick={handleConfirm}
            disabled={saving}
          >
            {saving ? 'Spremanje...' : 'Potvrdi dolazak'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RsvpModal;
